import { styled } from 'styled-components';
import { Card, Flex, Heading, ShieldIcon, Text, THEME_UTILITIES, useBrand } from '@cof/plastic-components';
import config from '@cof/ukweb-config/lib/clientConfig';
import PropTypes from 'prop-types';

import { withText } from '../../text/textStore.js';
import useIsPtd from '../../utilities/useIsPtd';
import partners from '../../partners.js';
import GenericBanner from './GenericBanner.jsx';
import InstantIssuanceBanner from './InstantIssuanceBanner.jsx';
import FormErrors from '../FormErrors/FormErrors.jsx';

const FormCard = styled(Card)`
    box-shadow: unset;
    padding: 0;
`;

const GradientText = styled.span`
    background: linear-gradient(96.87deg, #ff8791 22.28%, #ffe433 97.69%);
    -webkit-text-fill-color: transparent;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-background-clip: text;
`;

const DesktopBreak = styled.br`
    display: none;
    ${({ theme }: any) => theme.breakpoints.mq.tabletMin} {
        display: block;
    }
`;

const PtdTitle1Line = styled.span`
    display: inline-block;
    min-width: max-content;
`;

const { useColor } = THEME_UTILITIES;

const LARGE_FONT_BRANDS = ['capitalOne', 'asos', 'luma'];

const HEADING_COLORS = {
    [partners.ASOS]: 'ASOS.purple350',
    [partners.CAPITAL_ONE]: 'global.white',
    [partners.LITTLEWOODS]: 'littlewoods.purple550',
    [partners.LUMA]: 'luma.purple500'
};

const isInstantIssuance = () => {
    return config.get('features.instantIssuance');
};

const getBorderBackground = (brand: any) =>
    brand === 'capitalOne' ? 'linear-gradient(170deg, #00c3b3, #ffe433)' : 'black';

const getMobileFontSize = (brand: any) =>
    LARGE_FONT_BRANDS.includes(brand) ? 'headingLargeMobile' : 'headingMediumMobile';

const getHeaderPadding = (isPtd: boolean) => (isPtd ? ['sm', null, 'md'] : ['sm', null, 'lg']);

const Title2 = ({ brand, text, isPtd }: any) => {
    if (isPtd) {
        return (
            <span data-qa-id="ptd-title2" data-qm-allow>
                {text({ path: 'PtdTitle2' })}
            </span>
        );
    }
    if (brand === 'capitalOne') {
        return (
            <GradientText data-qa-id="gradient-title2" data-qm-allow>
                {text({ path: 'Title2' })}
            </GradientText>
        );
    }
    const fontWeights = {
        very: 'inherit',
        luma: '600'
    };
    return (
        <Text
            // @ts-expect-error TS2322: Type '"inherit"' is not assignable to type 'keyof FontSizeKeys | null'.
            fontSize={['inherit', 'inherit']}
            // @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '
            fontWeight={fontWeights[brand] || 'medium'}
            data-qa-id="block-title2"
            data-qm-allow
        >
            {text({ path: 'Title2' })}
        </Text>
    );
};

Title2.propTypes = {
    brand: PropTypes.string.isRequired,
    text: PropTypes.func.isRequired,
    isPtd: PropTypes.bool.isRequired
};

const SimpleHeader = ({ Text: text = false }) => {
    const headingLine1Colors = useColor(HEADING_COLORS);
    const brand = useBrand();
    const borderBackground = getBorderBackground(brand);
    const Banner = isInstantIssuance() ? InstantIssuanceBanner : GenericBanner;
    const isPtd = useIsPtd();

    return (
        // @ts-expect-error TS2769: No overload matches this call.
        <FormCard data-qa-id="simple-header" backgroundColor="transparent">
            <Flex flexDirection={'column'} maxWidth="100%" alignItems="center" data-qa-id="generic-simple-header">
                <Flex
                    flexDirection={'column'}
                    // @ts-expect-error TS2769: No overload matches this call.
                    pb={getHeaderPadding(isPtd)}
                    textAlign={['left', null, 'center']}
                    width="100%"
                    alignItems="center"
                >
                    <Heading
                        // @ts-expect-error TS2322: Type ' children: any[]; as: string; textAlign: ("left" | "center" | null)[]; color: AllowedColors; p
                        as="h1"
                        textAlign={['left', null, 'center']}
                        color={headingLine1Colors}
                        pb={'2.6rem'}
                        data-qa-id="heading"
                        fontSize={[getMobileFontSize(brand), null, 'headingMediumDesktop']}
                        data-qm-allow
                        style={{ width: '100%' }}
                        paddingLeft={['sm', 0, 0]}
                    >
                        {isPtd ? (
                            <PtdTitle1Line data-qa-id="ptd-title1" data-qm-allow>
                                {/* @ts-expect-error TS2349: This expression is not callable. */}
                                {text({ path: 'PtdTitle1' })}
                            </PtdTitle1Line>
                        ) : (
                            // @ts-expect-error TS2349: This expression is not callable.
                            text({ path: 'Title1' })
                        )}
                        {/* @ts-expect-error TS2367: This comparison appears to be unintentional because the types 'BrandProps' and '"asos"' have no overlap. */}
                        {brand === 'asos' ? (
                            <>
                                {' '}
                                <DesktopBreak aria-hidden={true} />
                            </>
                        ) : (
                            <br aria-hidden={true} />
                        )}
                        <Title2 brand={brand} text={text} isPtd={isPtd} />
                    </Heading>
                    <Banner $borderBackground={borderBackground} />
                    {isPtd && (
                        <Flex alignItems="center" justifyContent="center" pt="sm" data-qa-id="ptd-no-impact">
                            <ShieldIcon color="global.white" />
                            <Text pl="xs" pb={0} color="global.white" data-qm-allow>
                                {/* @ts-expect-error TS2349: This expression is not callable. */}
                                {text({ path: 'PtdNoImpact' })}
                            </Text>
                        </Flex>
                    )}
                </Flex>
                <FormErrors />
            </Flex>
        </FormCard>
    );
};

SimpleHeader.propTypes = {
    Text: PropTypes.func.isRequired
};

const WrappedSimpleHeader = withText('SimpleHeader')(SimpleHeader);

WrappedSimpleHeader.displayName = 'SimpleHeader';

export default WrappedSimpleHeader;
